import { countOperationalRdvOnDay } from '../../utils/equipeRdvRules';
import { formatParisDayHeader } from '../../utils/equipeWeekParis';
import {
  DISPLAY_KIND,
  getDayPlanningDisplayState,
} from '../../utils/equipeDayPlanningDisplayState';

const CELL_TONE = {
  [DISPLAY_KIND.HOURS]: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  [DISPLAY_KIND.CUSTOM_HOURS]: 'bg-violet-50 text-violet-900 border-violet-200',
  [DISPLAY_KIND.REST]: 'bg-slate-100 text-slate-700 border-slate-200',
  [DISPLAY_KIND.UNPLANNED]: 'bg-gray-50 text-gray-400 border-gray-100',
  [DISPLAY_KIND.ABSENT]: 'bg-gray-100 text-gray-600 border-gray-200',
  [DISPLAY_KIND.LEAVE]: 'bg-amber-50 text-amber-900 border-amber-200',
  [DISPLAY_KIND.UNAVAILABLE]: 'bg-amber-50 text-amber-900 border-amber-200',
};

function MonthStaffCell({ display, rdvCount, onClick }) {
  const tone = CELL_TONE[display.kind] || CELL_TONE[DISPLAY_KIND.UNPLANNED];
  const Tag = onClick ? 'button' : 'div';
  const showVioletDot =
    display.showExceptionBadge && display.kind === DISPLAY_KIND.CUSTOM_HOURS;

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`relative flex min-h-[52px] w-full flex-col items-start justify-center rounded-lg border px-2 py-1.5 text-left text-xs font-medium leading-snug ${tone} ${
        onClick ? 'cursor-pointer hover:ring-2 hover:ring-violet-200' : ''
      }`}
      title={display.label}
    >
      {showVioletDot ? (
        <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-violet-500" aria-hidden />
      ) : null}
      <span className="line-clamp-2 pr-2">{display.label}</span>
      {rdvCount > 0 ? (
        <span className="mt-0.5 text-[10px] font-semibold text-violet-700">{rdvCount} RDV</span>
      ) : null}
    </Tag>
  );
}

/**
 * Vue mois V2 — une ligne par date, une colonne par professionnel(le).
 */
export default function TeamMonthPlanningGrid({
  members = [],
  monthDays = [],
  selectedId,
  onSelectMember,
  horairesByInstructeur = {},
  absencesByInstructeur = {},
  dateExceptionsByInstructeur = {},
  rdvList = [],
  loading = false,
  onCellClick = null,
}) {
  const prestataires = (members || []).filter((m) => m.isIntervenant && m.instructeurId);
  const staffMinWidth = Math.max(prestataires.length, 1) * 128;

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!prestataires.length) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">
        Aucune personne ne réalise de prestations pour le moment.
      </p>
    );
  }

  return (
    <div className="max-h-[min(72vh,640px)] overflow-y-auto overflow-x-auto rounded-lg border border-gray-100">
      <table className="w-full min-w-full border-collapse text-left" style={{ minWidth: staffMinWidth + 140 }}>
        <thead className="sticky top-0 z-20 bg-white shadow-sm">
          <tr>
            <th className="sticky left-0 z-30 min-w-[132px] bg-white px-3 py-2 text-xs font-bold uppercase tracking-wide text-gray-500">
              Date
            </th>
            {prestataires.map((m) => {
              const isSelected = selectedId === m.id;
              return (
                <th key={m.id} className="min-w-[120px] px-2 py-2 text-xs font-semibold text-gray-800">
                  <button
                    type="button"
                    onClick={() => onSelectMember?.(m.id, 'planning')}
                    className={`w-full truncate rounded-lg px-2 py-1 text-left ${
                      isSelected ? 'bg-violet-50 text-violet-800 ring-1 ring-violet-200' : 'hover:bg-gray-50'
                    }`}
                  >
                    {m.prenom} {m.nom}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {monthDays.map((day) => (
            <tr key={day.ymd} className="border-t border-gray-50 align-top">
              <td className="sticky left-0 z-10 bg-white px-3 py-2 text-xs font-semibold text-gray-800">
                {formatParisDayHeader(day.date)}
              </td>
              {prestataires.map((m) => {
                const instructeurId = m.instructeurId;
                const config = horairesByInstructeur[instructeurId];
                const absences = absencesByInstructeur[instructeurId] || [];
                const dateExceptions = dateExceptionsByInstructeur[instructeurId] || null;
                const display = getDayPlanningDisplayState({
                  horairesConfig: config,
                  absences,
                  jourSemaine: day.jourSemaine,
                  ymd: day.ymd,
                  dateExceptionsByDate: dateExceptions,
                });
                const rdvCount = countOperationalRdvOnDay(
                  (rdvList || []).filter((r) => r.instructeurId === instructeurId),
                  day.ymd,
                );
                return (
                  <td key={m.id} className="px-2 py-1.5">
                    <MonthStaffCell
                      display={display}
                      rdvCount={rdvCount}
                      onClick={
                        onCellClick
                          ? () =>
                              onCellClick({
                                member: m,
                                day,
                                cell: display.cell,
                                instructeurId,
                              })
                          : null
                      }
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
